// Defensive allow-list applied to every analytics.track()/page() call so a
// single careless call site (e.g. accidentally passing a search query or a
// token) can't leak PII to GA4/PostHog. This is a safety net, not the only
// line of defense - callers should still only pass safe metadata.
import type { AnalyticsProperties } from "./types";

const SENSITIVE_KEY_PATTERN = /token|password|secret|email|ip$|ipaddress|address|phone/i;
const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/;
// Heuristic for "this looks like free text a user typed", not a bounded
// enum/id/count: long strings, or strings containing whitespace, are
// dropped rather than guessed at. A single kana/kanji character (e.g. a
// "symbol" property identifying which character was practiced/played) is
// legitimate bounded product data, not free text, so non-ASCII alone isn't
// disqualifying below NON_ASCII_LENGTH_LIMIT - only past it (a real word or
// sentence) does it get treated as free text.
const FREE_TEXT_LENGTH_LIMIT = 40;
const NON_ASCII_LENGTH_LIMIT = 4;

function looksLikeFreeText(value: string): boolean {
    if (value.length > FREE_TEXT_LENGTH_LIMIT) return true;
    if (/\s/.test(value)) return true;
    // eslint-disable-next-line no-control-regex -- intentional non-ASCII check
    if (/[^\x00-\x7F]/.test(value) && value.length > NON_ASCII_LENGTH_LIMIT) return true;
    return false;
}

export function sanitizeProperties(properties?: AnalyticsProperties | null): AnalyticsProperties {
    if (!properties || typeof properties !== "object") return {};

    const safe: AnalyticsProperties = {};
    for (const [key, value] of Object.entries(properties)) {
        if (SENSITIVE_KEY_PATTERN.test(key)) {
            if (process.env.NODE_ENV !== "production") {
                console.warn(`[analytics] dropped property "${key}" - name looks sensitive`);
            }
            continue;
        }

        if (typeof value === "string") {
            if (EMAIL_PATTERN.test(value) || looksLikeFreeText(value)) {
                if (process.env.NODE_ENV !== "production") {
                    console.warn(`[analytics] dropped property "${key}" - value looks like free text or an email`);
                }
                continue;
            }
        }

        if (value === undefined) continue;
        safe[key] = value;
    }
    return safe;
}
